import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate }                                 from 'react-router-dom';
import { DocumentReportIcon, TrashIcon }               from '@heroicons/react/outline';
import {
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';

import {
  requestDismissDealStatusReminder,
  requestDismissMultipleDealStatusReminders,
  requestUpdateDealStatusReminder,
}                                     from '@modules/ContactManager/components/Header/requests';
import { getMoment, withQueryParams } from '@shared/commonFunctions';
import { AppPopover }                 from '@shared/components/AppPopover';
import { useAnchor }                  from '@shared/hooks/useAnchor';
import { EDismissButtonTypes }        from '@shared/interfaces';
import { ButtonWrapper, ReminderDetails, ReminderFooter, ReminderRow, ReminderRowGroup } from '@styles/Layout/PopOversStyles';
import { useTabs } from '@shared/hooks/useTabs';

import { HeaderControlButton } from '../HeaderControlButton';

export interface IDealStatusReminders {
  id: string;
  userId: string;
  contactId: string;
  contactNumericId: number;
  contactFirstName: string;
  contactLastName: string;
  dealId: string;
  dealNumericId: number;
  lenderName: string;
  dealStatus: string;
  reminderDate: string;
  actionDate: string;
}

interface IDealStatusRemindersProps {
  reminders: IDealStatusReminders[];
  reloadReminders: () => void;
}

export const DealStatusRemindersPreview = ({ reminders, reloadReminders }: IDealStatusRemindersProps) => {
  const theme = useTheme();
  const [anchor, toggleAnchor] = useAnchor();
  const { openTab } = useTabs();
  const navigate = useNavigate();

  const [snoozeTime, setSnoozeTime] = useState<{ [key: string]: string | object }>({});
  const [selectedReminders, setSelectedReminders] = useState<string[]>([]);

  const dismissButtonTitle = useMemo(
    () => (selectedReminders.length ? EDismissButtonTypes.DismissSelected : EDismissButtonTypes.DismissAll),
    [selectedReminders],
  );

  const handleChange = useCallback(
    async(e: SelectChangeEvent<string | object>, reminderId: string) => {
      await setSnoozeTime((prev) => ({ ...prev, [reminderId]: e.target.value }));
      const date = new Date().toISOString();
      const newTime = getMoment(date).add(Number(e.target.value), 'minutes');
      await requestUpdateDealStatusReminder(reminderId, newTime.toISOString());
      reloadReminders();
    },
    [reloadReminders],
  );

  const onReminderClick = useCallback((row: IDealStatusReminders) => {
    const route = withQueryParams('/contact-manager', { id: row.contactId, tab: 3, dealId: row.dealId }, false);
    const title = `${row.contactFirstName} ${row.contactLastName}`;
    openTab({ title, route });
    navigate(route);
    toggleAnchor();
  }, []);

  const onViewAllClick = useCallback((url: string) => {
    openTab({ title: 'Deal Status Reminders', route: url });
    navigate(url);
    toggleAnchor();
  }, []);

  const dealStatusReminderUrl = '/my-lc-queues/deal-lender-status-notification';

  const reminderData = useMemo<IDealStatusReminders[]>(() => reminders?.slice(0, 4), [reminders]);

  const onCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>, id: string) => {
    if (event.target.checked) {
      setSelectedReminders((prev) => [...prev, id]);
    } else {
      setSelectedReminders((prev) => prev.filter((reminderId) => reminderId !== id));
    }
  }, []);

  const onDismissButtonClick = useCallback(async() => {
    if (reminders.length) {
      if (selectedReminders.length) {
        await requestDismissMultipleDealStatusReminders({ reminderIds: selectedReminders });
      } else {
        await requestDismissMultipleDealStatusReminders({ reminderIds: reminders.map((reminder) => reminder.id) });
      }

      setSelectedReminders([]);
      reloadReminders();
    }
  }, [selectedReminders, reminders, reloadReminders]);

  const onToggleAnchor = useCallback(() => {
    toggleAnchor();
    setSelectedReminders([]);
  }, [toggleAnchor]);

  const getDecisionStatus = useCallback((status: string) => {
    if (!status) {
      return '';
    }

    const normalized = status.toLowerCase();
    if (normalized.includes('approved')) {
      return 'Approved';
    }
    if (normalized.includes('counter')) {
      return 'Countered';
    }
    if (normalized.includes('declined')) {
      return 'Declined';
    }

    return status;
  }, []);

  return (
    <>
      <AppPopover title="Deal Status Reminders" anchor={anchor} toggleAnchor={onToggleAnchor}>
        {reminders && (
          <ButtonWrapper isAllBtn={dismissButtonTitle === EDismissButtonTypes.DismissAll} onClick={onDismissButtonClick}>{dismissButtonTitle}</ButtonWrapper>
        )}

        {reminderData?.map((item: IDealStatusReminders) => (
          <ReminderRow key={item.id}>
            <Checkbox checked={selectedReminders.includes(item.id)} onChange={(e) => onCheckboxChange(e, item.id)} />
            <ReminderDetails onClick={() => onReminderClick(item)}>
              <Typography variant="subtitle1">{`${item.contactFirstName} ${item.contactLastName}`}</Typography>
              <Typography variant="caption">{`(${item.dealNumericId || item.dealId}) ${item.lenderName || '-'} ${getDecisionStatus(item.dealStatus) || '-'}`}</Typography>
              <Typography variant="caption">
                {`${getMoment((item.actionDate || item.reminderDate).toString()).format('MM/DD/YYYY')} ${getMoment((item.actionDate || item.reminderDate).toString()).format('hh:mm A')}`}
              </Typography>
            </ReminderDetails>

            <ReminderRowGroup>
              <FormControl fullWidth>
                <Select
                  autoWidth
                  displayEmpty
                  labelId="snooze-select-label"
                  value={snoozeTime[item.id] || ''}
                  label="Snooze"
                  id="snooze-label"
                  onChange={(e) => handleChange(e, item.id)}
                >
                  <MenuItem sx={{ display: 'none' }} value={''}>
                    Snooze
                  </MenuItem>
                  <MenuItem value={5}>5 minutes</MenuItem>
                  <MenuItem value={10}>10 minutes</MenuItem>
                  <MenuItem value={30}>30 minutes</MenuItem>
                  <MenuItem value={60}>1 hour</MenuItem>
                </Select>
                <IconButton
                  onClick={async() => {
                    await requestDismissDealStatusReminder(item.id);
                    reloadReminders();
                  }}
                >
                  <TrashIcon width={24} color={theme.palette.secondary.dark} />
                </IconButton>
              </FormControl>
            </ReminderRowGroup>
          </ReminderRow>
        ))}

        <ReminderFooter onClick={() => onViewAllClick(dealStatusReminderUrl)}>View All</ReminderFooter>
      </AppPopover>

      <HeaderControlButton count={reminders?.length} icon={DocumentReportIcon} onClick={toggleAnchor} />
    </>
  );
};
